/**
 * Wizard step 4 — the animated per-item review: summary bar, staggered
 * cards, set name + optional load-into-draft, Create N accepted.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { AnimatePresence } from 'framer-motion';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui/form/input';

import { ReviewItemCard, ReviewSummaryBar } from '../../review';
import type { AiWizardState } from '../use-ai-wizard';

export interface ReviewStepProps {
  wizard: AiWizardState;
  /** Present when the wizard was opened from a campaign drawer. */
  canApplyToDraft: boolean;
  busy: boolean;
  onCreate: (input: { name: string; applyToDraft: boolean }) => void;
}

const ReviewStep: FC<ReviewStepProps> = ({
  wizard,
  canApplyToDraft,
  busy,
  onCreate,
}) => {
  const [name, setName] = useState('');
  const [applyToDraft, setApplyToDraft] = useState(false);
  const accepted = wizard.items.filter((item) => item.accepted).length;

  return (
    <div className="space-y-3">
      <ReviewSummaryBar
        accepted={accepted}
        total={wizard.items.length}
        onAcceptAll={() => wizard.setAllAccepted(true)}
        onRejectAll={() => wizard.setAllAccepted(false)}
      />
      <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
        <AnimatePresence initial>
          {wizard.items.map((item, index) => (
            <ReviewItemCard
              key={item.id}
              item={item}
              index={index}
              onAccept={(value) => wizard.setAccepted(item.id, value)}
            />
          ))}
        </AnimatePresence>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={
            wizard.options.target === 'package' ? 'Package name' : 'Set name'
          }
          className="h-8 w-56 text-xs"
        />
        {canApplyToDraft ? (
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={applyToDraft}
              onChange={(e) => setApplyToDraft(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Also load into this campaign&apos;s draft
          </label>
        ) : null}
        <span className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => wizard.setStep('paste')}>
            ← Back
          </Button>
          <Button
            size="sm"
            disabled={accepted === 0 || !name.trim() || busy}
            onClick={() => onCreate({ name: name.trim(), applyToDraft })}
          >
            {busy ? 'Creating…' : `Create ${accepted} accepted`}
          </Button>
        </span>
      </div>
    </div>
  );
};

export default ReviewStep;
